import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

// Side-effect imports: Babylon is consumed a-la-carte so the bundle stays small,
// which means the features we use must be registered explicitly.
import '@babylonjs/core/Meshes/Builders/boxBuilder';
import '@babylonjs/core/Meshes/Builders/groundBuilder';
// The Build screen's reference grid.
import '@babylonjs/core/Meshes/Builders/linesBuilder';
import '@babylonjs/core/Materials/standardMaterial';
// `scene.pick`, which is how the Build screen turns a cursor into a cell. Without it
// every pick silently misses -- Babylon warns to the console and returns no hit rather
// than throwing, so the screen simply stops answering clicks.
import '@babylonjs/core/Culling/ray';

export interface SceneContext {
  engine: Engine;
  scene: Scene;
  camera: ArcRotateCamera;
}

/** Boots Babylon on `canvas` and returns the pieces the game layers build on. */
export function createScene(canvas: HTMLCanvasElement): SceneContext {
  const engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true }, true);

  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.03, 0.03, 0.05, 1);

  const camera = new ArcRotateCamera('camera', -Math.PI / 2, Math.PI / 3.2, 48, Vector3.Zero(), scene);
  camera.lowerRadiusLimit = 8;
  camera.upperRadiusLimit = 140;
  camera.upperBetaLimit = Math.PI / 2.1;
  camera.wheelDeltaPercentage = 0.02;
  camera.attachControl(canvas, true);

  const ambient = new HemisphericLight('ambient', new Vector3(0, 1, 0), scene);
  ambient.intensity = 0.45;
  ambient.diffuse = new Color3(0.7, 0.72, 0.85);
  ambient.groundColor = new Color3(0.16, 0.13, 0.18);

  const key = new DirectionalLight('key', new Vector3(-0.4, -1, 0.6), scene);
  key.intensity = 1.1;
  key.diffuse = new Color3(1, 0.93, 0.78);

  engine.runRenderLoop(() => scene.render());

  const onResize = () => engine.resize();
  window.addEventListener('resize', onResize);
  scene.onDisposeObservable.addOnce(() => window.removeEventListener('resize', onResize));

  return { engine, scene, camera };
}
